import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Truck, Package, Users, DollarSign, TrendingUp, 
  Clock, CheckCircle, XCircle, AlertCircle, Eye,
  UserCheck, UserX, FileText
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const AdminLogistics = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [operators, setOperators] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [bookingFilter, setBookingFilter] = useState('all');

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'operators') {
      fetchOperators();
    } else if (activeTab === 'bookings') {
      fetchBookings();
    }
  }, [activeTab, operatorFilter, bookingFilter]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/logistics/dashboard');
      setDashboard(response.data.dashboard);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load dashboard',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const params = operatorFilter !== 'all' ? `?status=${operatorFilter}` : '';
      const response = await api.get(`/admin/logistics/operators${params}`);
      setOperators(response.data.operators || []);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const params = bookingFilter !== 'all' ? `?status=${bookingFilter}` : '';
      const response = await api.get(`/admin/logistics/bookings${params}`);
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const approveOperator = async (operatorId) => {
    try {
      await api.post(`/admin/logistics/operators/${operatorId}/approve`);
      toast({
        title: 'Operator Approved',
        description: 'The operator can now accept jobs'
      });
      fetchOperators();
      fetchDashboard();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to approve operator',
        variant: 'destructive'
      });
    }
  };

  const rejectOperator = async (operatorId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await api.post(`/admin/logistics/operators/${operatorId}/reject`, null, {
        params: { reason }
      });
      toast({
        title: 'Operator Rejected',
        description: 'The operator has been notified'
      });
      fetchOperators();
      fetchDashboard();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reject operator',
        variant: 'destructive'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'accepted': return 'bg-blue-100 text-blue-700';
      case 'in_transit': return 'bg-orange-100 text-orange-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <AdminLayout>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="h-7 w-7 text-orange-500" />
            Logistics Management
          </h1>
          <p className="text-gray-600">Manage cargo operators and shipments</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { key: 'dashboard', label: 'Dashboard', icon: TrendingUp },
          { key: 'operators', label: 'Operators', icon: Users },
          { key: 'bookings', label: 'Bookings', icon: Package }
        ].map((tab) => (
          <Button
            key={tab.key}
            variant={activeTab === tab.key ? 'default' : 'ghost'}
            onClick={() => setActiveTab(tab.key)}
            className={activeTab === tab.key ? 'bg-orange-500 hover:bg-orange-600' : ''}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.bookings?.total || 0}</p>
                  <p className="text-sm text-gray-600">Total Bookings</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.bookings?.byStatus?.delivered || 0}</p>
                  <p className="text-sm text-gray-600">Delivered</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.operators?.approved || 0}</p>
                  <p className="text-sm text-gray-600">Active Operators</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">K{dashboard.revenue?.total?.toFixed(0) || 0}</p>
                  <p className="text-sm text-gray-600">Revenue</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Status Breakdown */}
          <div className="grid grid-cols-2 gap-6">
            <Card className="p-4">
              <h3 className="font-semibold mb-4">Bookings by Status</h3>
              <div className="space-y-2">
                {Object.entries(dashboard.bookings?.byStatus || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(status)}`}>
                      {status.replace('_', ' ')}
                    </span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold mb-4">Operators Overview</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Operators</span>
                  <span className="font-bold">{dashboard.operators?.total || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Pending Approval</span>
                  <span className="font-bold text-yellow-600">{dashboard.operators?.pending || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Currently Online</span>
                  <span className="font-bold text-green-600">{dashboard.operators?.online || 0}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Operators Tab */}
      {activeTab === 'operators' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((filter) => (
              <Button
                key={filter}
                variant={operatorFilter === filter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setOperatorFilter(filter)}
                className={operatorFilter === filter ? 'bg-orange-500' : ''}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </Button>
            ))}
          </div>

          {operators.length === 0 ? (
            <Card className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No operators found</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {operators.map((operator) => (
                <Card key={operator.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <Truck className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{operator.name}</h4>
                        <p className="text-sm text-gray-600">{operator.phone}</p>
                        <p className="text-sm text-gray-500">
                          {operator.vehicleMake} {operator.vehicleModel} • {operator.plateNumber}
                        </p>
                        <p className="text-xs text-gray-400">
                          Capacity: {operator.loadCapacityKg} kg
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(operator.approvalStatus)}`}>
                        {operator.approvalStatus}
                      </span>
                      {operator.approvalStatus === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => approveOperator(operator.id)}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <UserCheck className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => rejectOperator(operator.id)}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                      {operator.approvalStatus === 'approved' && (
                        <span className={`text-xs ${operator.isOnline ? 'text-green-600' : 'text-gray-400'}`}>
                          {operator.isOnline ? '● Online' : '○ Offline'}
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'accepted', 'in_transit', 'delivered', 'cancelled'].map((filter) => (
              <Button
                key={filter}
                variant={bookingFilter === filter ? 'default' : 'outline'}
                size="sm"
                onClick={() => setBookingFilter(filter)}
                className={bookingFilter === filter ? 'bg-orange-500' : ''}
              >
                {filter.replace('_', ' ').charAt(0).toUpperCase() + filter.replace('_', ' ').slice(1)}
              </Button>
            ))}
          </div>

          {bookings.length === 0 ? (
            <Card className="p-8 text-center">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No bookings found</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking) => (
                <Card key={booking.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-mono font-bold text-orange-600">{booking.trackingCode}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(booking.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(booking.status)}`}>
                        {booking.status?.replace('_', ' ')}
                      </span>
                      <p className="text-lg font-bold text-green-600 mt-1">K{booking.price?.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">From</p>
                      <p className="font-medium">{booking.pickupAddress}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">To</p>
                      <p className="font-medium">{booking.dropoffAddress}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t text-sm">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-500">
                        {booking.cargo?.weightKg} kg • {booking.cargo?.category}
                      </span>
                      <span className="text-gray-500">
                        {booking.vehicleType?.replace('_', ' ')}
                      </span>
                    </div>
                    {booking.operatorDetails && (
                      <span className="text-gray-600">
                        Operator: {booking.operatorDetails.name}
                      </span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminLogistics;
